import React, {useCallback, useEffect, useState} from "react";
import {Col, Form, Row, Spinner} from "react-bootstrap";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import BarChart from "../../BarChart/BarChart";
import DataMultiSelector from "../DataMultiSelector/DataMultiSelector";
import Sort from "../SortComponent/Sort";
import {
    ALL_THE_REST_TITLE,
    BAR_CHAR_ENTITIES_LEN_LIMIT,
    COOLING_TOWER_TITLE,
    MAIN_INLET_TITLE,
    MAX_SELECTION_LIMIT,
    POTENTIAL_WASTE,
    WATER_CONSUMPTION_NAME
} from "../../../Constants/constants";

import {
    filterPreviousSelectedEntities, getLastMonthEndDate, getLastMonthStartDate, getResponseTypeList, gqlDateFormat
} from "../Utils/utils";
import {apolloClientReports} from "../../../GraphQL/config";
import {
    QUERY_GET_CONSUMPTION_AND_POTENTIAL_WASTE
} from "../../../GraphQL/ReportGenerator/queries";
import moment from "moment/moment";
import convert, {DEFAULT_VOLUME_UNIT} from "../MeasurementText/MeasurementTextConverter";


export default function ConsumptionAndPotentialWaste(props) {
    const [includeTypes, setIncludeTypes] = useState({
        'MAIN_INLET': false, 'COOLING_TOWER': false, 'OTHER': true
    });

    const [isLoading, setIsLoading] = useState(true);
    const [entities, setEntities] = useState();
    const [chartData, setChartData] = useState();
    const BAR_CHART_HEIGHT = 240;
    const [startDate, setStartDate] = useState(getLastMonthStartDate());
    const [endDate, setEndDate] = useState(getLastMonthEndDate());
    const [selectedEntities, setSelectedEntities] = useState();
    const [dateApplied, setDateApplied] = useState(false);
    const [filteredEntities, setFilteredEntities] = useState([]);
    const [previousSelectedValues, setPreviousSelectedValues] = useState(null)
    const [csvData, setCsvData] = useState([]);
    const [refreshSort, setRefreshSort] = useState(false);
    const [volumeUnit, setVolumeUnit] = useState(DEFAULT_VOLUME_UNIT);
    const entityType = props.entityLabel;
    const [chartMinHeight, setChartMinHeight] = useState(200);
    const [chartPointWidth, setChartPointWidth] = useState(18)
    const [systemTypeGroups, setSystemTypeGroups] = useState(['OTHER'])

    const sortOptions = [
        {value: 'name', label: `${props.entityType} Name`},
        {value: 'consumption', label: 'Actual Consumption'},
        {value: 'potentialWaste', label: 'Potential Waste'},
        {value: 'sum', label: 'Actual + Potential'}
    ]

    const [selectedSort, setSelectedSort] = useState(sortOptions[0].value);

    const getWaterConsumptionAndPotentialWasteDataFromDB = () => {
        apolloClientReports.query({
            query: QUERY_GET_CONSUMPTION_AND_POTENTIAL_WASTE, variables: {
                from: gqlDateFormat(startDate),
                to: gqlDateFormat(endDate),
                entityType: entityType,
                systemTypeGroups: systemTypeGroups
            }
        }).then(res => {
            let response = res?.data?.waterConsumptionAndLeakWasteEntityChart;
            if (!response) {
                console.error(`Data from getWaterConsumptionAndPotentialWaste is empty`);
            } else {
                setVolumeUnit(convert(response.measureUnit.volumeUnit));
                let responseEntities = [...response?.entities].sort((a, b) => b.data.waterConsumption - a.data.waterConsumption);
                setEntities(responseEntities?.map((entity) => entity));
                const types = response.entities.map(o => o.type[0])
                const typeArr = getResponseTypeList(types)
                if (!selectedEntities || dateApplied) {
                    setDateApplied(false);
                    let currentValuesArr = responseEntities.map((entity) => entity).slice(0, MAX_SELECTION_LIMIT)
                    if (previousSelectedValues) {
                        let PreviousValuesArr = filterPreviousSelectedEntities(typeArr, currentValuesArr, previousSelectedValues)
                        PreviousValuesArr.forEach((value) => {
                            currentValuesArr.push(value)
                        })
                    }
                    setSelectedEntities(currentValuesArr);
                } else {
                    setRefreshSort(!refreshSort);
                }
                setIsLoading(false);
            }
        })
    }

    const calculateChartParams = (filteredEntities) => {
        if (!filteredEntities) {
            return
        }
        if (filteredEntities.length <= BAR_CHAR_ENTITIES_LEN_LIMIT) {
            setChartPointWidth(18)
            setChartMinHeight(60 * filteredEntities.length)
        } else {
            setChartPointWidth(10)
            setChartMinHeight(20 * filteredEntities.length)
        }
    }

    const buildCsvData = (responseData) => {
        let csvData = [];
        let headers = ['', 'From', 'To', entityType, 'Water consumption', 'Potential waste'];
        csvData.push(headers);

        responseData.forEach((entity, index) => {
            let row = [];
            row.push('');
            if (index === 0) {
                row.push(moment(startDate).format('LL'), moment(endDate).format('LL'));
            } else {
                row.push('', '');
            }
            row.push(entity.name, entity.data.waterConsumption, entity.data.potentialWaste);
            csvData.push(row);
        });

        return csvData;
    }

    useEffect(() => {
        setIsLoading(true);
        getWaterConsumptionAndPotentialWasteDataFromDB();
    }, [dateApplied]);


    useEffect(() => {
        setIsLoading(true);
        getWaterConsumptionAndPotentialWasteDataFromDB();
    }, [systemTypeGroups]);

    useEffect(() => {

        switch (selectedSort) {
            case 'consumption':
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(b.data.waterConsumption) - parseFloat(a.data.waterConsumption)));
                break;
            case 'potentialWaste':
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(b.data.potentialWaste) - parseFloat(a.data.potentialWaste)));
                break;
            case 'sum':
                setFilteredEntities(filteredEntities?.sort((a, b) => (parseFloat(a.data.waterConsumption + a.data.potentialWaste) - parseFloat(b.data.waterConsumption + b.data.potentialWaste))).reverse());
                break;
            default:
                setFilteredEntities(filteredEntities?.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase(), "de", {sensitivity: "base"})));
                break;
        }
        forceUpdate();
    }, [selectedSort, filteredEntities, refreshSort]);

    useEffect(() => {
        if (filteredEntities) {
            calculateChartParams(filteredEntities)
            setCsvData(buildCsvData(filteredEntities));
        }
    }, [filteredEntities, chartMinHeight, chartPointWidth])

    useEffect(() => {
        let keys = Object.keys(includeTypes);
        let filters = keys.filter((key) => includeTypes[key]);
        setFilteredEntities(selectedEntities)
        //setFilteredEntities(selectedEntities?.filter((entity) => filters.includes(entity.type) || (filters.includes('OTHER') && entity.type !== 'MAIN_INLET' && entity.type !== 'COOLING_TOWER')));
        setSystemTypeGroups(filters)
    }, [includeTypes, selectedEntities]);

    useEffect(() => {
        setChartData([{
            name: WATER_CONSUMPTION_NAME,
            data: filteredEntities?.length > 0 ? filteredEntities?.map((entity) => entity.data.waterConsumption) : [],
            color: '#229af6',
            dateRange: `${startDate} - ${endDate}`
        }, {
            name: POTENTIAL_WASTE,
            data: filteredEntities?.length > 0 ? filteredEntities?.map((entity) => entity.data.potentialWaste) : [],
            color: '#015777',
            dateRange: `${startDate} - ${endDate}`
        }]);
        forceUpdate();
    }, [JSON.stringify(filteredEntities), selectedEntities]);

    const [, setUpdateState] = useState();
    const forceUpdate = useCallback(() => setUpdateState({}), []);

    return (<>
        {(!entities || !selectedEntities || !chartData || !filteredEntities) &&
            <Spinner animation="border" variant="primary"/>}
        {entities && selectedEntities && chartData && filteredEntities && <div>
            <Row>
                <Col md={12}>
                    <p className={'card-title-main bold-title'}>{props.title}</p>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <DataMultiSelector
                        variant={props.variant}
                        setSelectedValues={setSelectedEntities}
                        entities={entities?.map((entity) => {
                            return {...entity, value: entity.id, label: entity.name}
                        })}
                        selectedValues={selectedEntities?.map((entity) => {
                            return {...entity, value: entity.id, label: entity.name}
                        })}/>
                </Col>
                <Col md={8} className={'DatePicker'}>
                    <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate}
                                     setDateApplied={setDateApplied}
                                     setEndDate={setEndDate} hasExportCsv csvData={csvData}
                                     title={'Water consumption and potential waste'}/>
                </Col>
            </Row>
            <br/>
            {isLoading && <Spinner animation="border" variant="primary"/>}
            {!isLoading && <>
                <Sort sortOptions={sortOptions} value={selectedSort} setSelectedSort={setSelectedSort}/>
                <Row>
                    <Col>
                        <BarChart
                            volumeUnit={volumeUnit}
                            height={BAR_CHART_HEIGHT}
                            horizontal
                            data={chartData}
                            categories={filteredEntities?.map((entity) => {
                                return entity.name
                            })}
                            scrollbar
                            numOfEntities={filteredEntities?.length}
                            minScrollHeight={chartMinHeight}
                            chartPointWidth={chartPointWidth}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Row className={'FontSize'}>
                            <Col md={3} className={'PullLeftInclude'}>
                                <p>SHOW:</p>
                            </Col>
                            <Col md={9} className={'PullLeftIncludeButtons'}>
                                <Form>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Check
                                                disabled={!includeTypes.COOLING_TOWER && !includeTypes.OTHER && includeTypes.MAIN_INLET}
                                                type='checkbox'
                                                label={MAIN_INLET_TITLE}
                                                onChange={() => {
                                                    setIncludeTypes({
                                                        ...includeTypes, MAIN_INLET: !includeTypes.MAIN_INLET
                                                    })
                                                    setPreviousSelectedValues(selectedEntities)
                                                    setSelectedEntities(null)
                                                }}
                                                checked={includeTypes.MAIN_INLET}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Check
                                                disabled={includeTypes.COOLING_TOWER && !includeTypes.OTHER && !includeTypes.MAIN_INLET}
                                                type='checkbox'
                                                label={COOLING_TOWER_TITLE}
                                                onChange={() => {
                                                    setIncludeTypes({
                                                        ...includeTypes, COOLING_TOWER: !includeTypes.COOLING_TOWER
                                                    })
                                                    setPreviousSelectedValues(selectedEntities)
                                                    setSelectedEntities(null)
                                                }}
                                                checked={includeTypes.COOLING_TOWER}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Check
                                                disabled={!includeTypes.COOLING_TOWER && includeTypes.OTHER && !includeTypes.MAIN_INLET}
                                                type='checkbox'
                                                label={ALL_THE_REST_TITLE}
                                                onChange={() => {
                                                    setIncludeTypes({
                                                        ...includeTypes, OTHER: !includeTypes.OTHER
                                                    })
                                                    setPreviousSelectedValues(selectedEntities)
                                                    setSelectedEntities(null)
                                                }}
                                                checked={includeTypes.OTHER}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </>}
        </div>}
    </>)
}
